/**
 * @file: SwipeableThreadRow.tsx
 *
 * Wraps a feed {@link ThreadRow} in a swipe-to-reveal action strip
 * (pin / mute / more), matching the chat list's idiom. Only the regular
 * `order` threads are wrapped — the scope-pinned essentials (system /
 * rewards / maintenance-plans) are permanent surfaces and stay
 * non-swipeable, so the list decides what to wrap.
 *
 * The actions are passed in — the row stays presentational, the list owns
 * the toggles and the "more" sheet (archive / delete). The strip auto-closes
 * on action tap. Pin/mute labels reflect current state so one button toggles
 * both ways.
 */

import { ThreadRow } from '@euler/app/flows/feed/components/ThreadRow';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FeedThreadInboxItem } from '@euler/model/feed';
import { FC, ReactNode, memo, useCallback, useRef } from 'react';
import { Animated, Text, TouchableOpacity, View } from 'react-native';
import Swipeable from 'react-native-gesture-handler/Swipeable';

const ACTION_WIDTH = 72;
// Amber, not the app-accent blue — the blue would merge into a selected
// (solid-blue) row's background when its swipe actions are revealed.
const PIN_COLOR = '#f59e0b';
const MUTE_COLOR = '#9ca3af';
// Neutral slate for "more" — distinct from mute's lighter grey, and not the
// alarming red (delete now lives inside the more sheet).
const MORE_COLOR = '#6b7280';

export const SwipeableThreadRow: FC<{
  thread: FeedThreadInboxItem;
  active?: boolean;
  /** Edge-to-edge bottom hairline (the pinned-section boundary). */
  fullWidthDivider?: boolean;
  onPress: () => void;
  onTogglePin: (thread: FeedThreadInboxItem) => void;
  onToggleMute: (thread: FeedThreadInboxItem) => void;
  onMore: (thread: FeedThreadInboxItem) => void;
}> = memo(
  ({
    thread,
    active,
    fullWidthDivider,
    onPress,
    onTogglePin,
    onToggleMute,
    onMore,
  }) => {
    const { rpx } = useResponsiveContext();
    const ref = useRef<Swipeable>(null);

    const isPinned = thread.pinnedAt != null;
    const isMuted = thread.mutedAt != null;

    const renderAction = useCallback(
      (
        progress: Animated.AnimatedInterpolation<number>,
        label: string,
        color: string,
        offset: number,
        onTap: () => void,
      ): ReactNode => {
        const translateX = progress.interpolate({
          inputRange: [0, 1],
          outputRange: [offset, 0],
        });
        return (
          <Animated.View style={{ flex: 1, transform: [{ translateX }] }}>
            <TouchableOpacity
              activeOpacity={0.7}
              style={{
                flex: 1,
                width: rpx(ACTION_WIDTH),
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: color,
              }}
              onPress={() => {
                ref.current?.close();
                onTap();
              }}
            >
              <Text
                style={{ color: '#fff', fontSize: rpx(12) }}
                numberOfLines={1}
              >
                {label}
              </Text>
            </TouchableOpacity>
          </Animated.View>
        );
      },
      [rpx],
    );

    const renderRightActions = useCallback(
      (progress: Animated.AnimatedInterpolation<number>) => (
        <View style={{ flexDirection: 'row', width: rpx(ACTION_WIDTH * 3) }}>
          {renderAction(
            progress,
            isPinned ? '取消置顶' : '置顶',
            PIN_COLOR,
            rpx(ACTION_WIDTH * 3),
            () => onTogglePin(thread),
          )}
          {renderAction(
            progress,
            isMuted ? '取消静音' : '静音',
            MUTE_COLOR,
            rpx(ACTION_WIDTH * 2),
            () => onToggleMute(thread),
          )}
          {renderAction(progress, '更多', MORE_COLOR, rpx(ACTION_WIDTH), () =>
            onMore(thread),
          )}
        </View>
      ),
      [
        rpx,
        renderAction,
        isPinned,
        isMuted,
        thread,
        onTogglePin,
        onToggleMute,
        onMore,
      ],
    );

    return (
      <Swipeable
        ref={ref}
        renderRightActions={renderRightActions}
        overshootRight={false}
        containerStyle={{ backgroundColor: '#eeeef0' }}
      >
        <ThreadRow
          thread={thread}
          active={active}
          fullWidthDivider={fullWidthDivider}
          onPress={onPress}
        />
      </Swipeable>
    );
  },
);
